import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'

export interface ExportData {
  title: string
  projectName?: string
  inputs: Array<{ label: string; value: string | number }>
  results: Array<{ label: string; value: string | number }>
  timestamp?: Date
}

export const exportToPDF = (data: ExportData) => {
  const doc = new jsPDF()
  const timestamp = data.timestamp || new Date()
  
  // Title
  doc.setFontSize(20)
  doc.setTextColor(74, 74, 74)
  doc.text(data.title, 14, 20)
  
  // Project Name (if provided)
  if (data.projectName) {
    doc.setFontSize(12)
    doc.setTextColor(100, 100, 100)
    doc.text(`Project: ${data.projectName}`, 14, 30)
  }
  
  // Date
  doc.setFontSize(10)
  doc.setTextColor(120, 120, 120)
  doc.text(`Generated: ${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}`, 14, data.projectName ? 38 : 30)
  
  // Input table
  const startY = data.projectName ? 45 : 37
  doc.setFontSize(14)
  doc.setTextColor(74, 74, 74)
  doc.text('Input Values', 14, startY)
  
  autoTable(doc, {
    startY: startY + 3,
    head: [['Parameter', 'Value']],
    body: data.inputs.map(item => [item.label, String(item.value)]),
    theme: 'grid',
    headStyles: { fillColor: [74, 74, 74] },
    margin: { left: 14 }
  })
  
  // Results table
  const finalY = (doc as any).lastAutoTable.finalY + 10
  doc.setFontSize(14)
  doc.text('Results', 14, finalY)
  
  autoTable(doc, {
    startY: finalY + 3,
    head: [['Result', 'Value']],
    body: data.results.map(item => [item.label, String(item.value)]),
    theme: 'grid',
    headStyles: { fillColor: [255, 140, 0] },
    margin: { left: 14 }
  })
  
  // Footer
  const pageHeight = doc.internal.pageSize.height
  doc.setFontSize(8)
  doc.setTextColor(150, 150, 150)
  doc.text('Generated by Concrete-Calculator.com', 14, pageHeight - 10)
  
  // Save
  const filename = `${data.title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.pdf`
  doc.save(filename)
}

export const exportToExcel = (data: ExportData) => {
  const timestamp = data.timestamp || new Date()
  
  // Create workbook
  const wb = XLSX.utils.book_new()
  
  // Prepare data
  const worksheetData = [
    [data.title],
    [],
    data.projectName ? ['Project Name:', data.projectName] : [],
    ['Generated:', `${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}`],
    [],
    ['INPUT VALUES'],
    ['Parameter', 'Value'],
    ...data.inputs.map(item => [item.label, item.value]),
    [],
    ['RESULTS'],
    ['Result', 'Value'],
    ...data.results.map(item => [item.label, item.value]),
    [],
    ['Generated by Concrete-Calculator.com']
  ].filter(row => row.length > 0)
  
  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(worksheetData)
  
  // Set column widths
  ws['!cols'] = [{ wch: 30 }, { wch: 20 }]
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Calculation Results')
  
  // Save
  const filename = `${data.title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.xlsx`
  XLSX.writeFile(wb, filename)
}

export const exportToText = (data: ExportData) => {
  const timestamp = data.timestamp || new Date()
  
  let content = `${data.title.toUpperCase()}\n${'='.repeat(50)}\n\n`
  
  if (data.projectName) {
    content += `Project Name: ${data.projectName}\n\n`
  }
  
  content += `Input Values:\n`
  data.inputs.forEach(item => {
    content += `- ${item.label}: ${item.value}\n`
  })
  
  content += `\nCalculated Results:\n`
  data.results.forEach(item => {
    content += `- ${item.label}: ${item.value}\n`
  })
  
  content += `\n${'='.repeat(50)}\nGenerated by Concrete-Calculator.com\nDate: ${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString()}\n`
  
  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${data.title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export const printResults = () => {
  window.print()
}
